import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { Label } from '@/features/label/components/label';
import { LabelGroup } from '@/features/label/types';
import { useDashboardSummary } from '@/features/statistic/api/get-dashboard-summary';
import { ExportByFilterDialog } from './export-by-filters-dialog';
import { Separator } from '@/components/ui/separator';
import { useState } from 'react';
import { useYear } from '@/features/settings/api/get-year';
import { useYears } from '@/features/settings/api/get-years';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Filter } from 'lucide-react';

export function Summary() {
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  useYear({
    config: {
      onSuccess: (data: number) => {
        setSelectedYear(data);
      }
    }
  });

  const { data: years = [] } = useYears({});
  const currentYear = new Date().getFullYear();
  const availableYears = years.map(y => y.yearId).filter(y => y <= currentYear).sort((a, b) => b - a);

  const { data: summary = {}, isLoading, refetch, dataUpdatedAt, isRefetching } = useDashboardSummary({ yearId: selectedYear ?? undefined });

  if (isLoading) return <Skeleton className="w-[500px] h-96" />;

  return (
    <Card className="w-full max-w-sm sm:max-w-md md:max-w-lg h-fit">
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          <Label name="overview_title" groupName={LabelGroup.MANAGER_DASHBOARD} />
          {availableYears.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="w-4 h-4 mr-1" />
                  År {selectedYear}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-30">
                <DropdownMenuLabel>
                  <Label name="Year" groupName={LabelGroup.ORGANIZER_EVENT_LIST} />
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {availableYears.map(year => (
                  <DropdownMenuCheckboxItem key={year} className="flex items-center gap-x-2" checked={selectedYear === year} onCheckedChange={() => setSelectedYear(year)}>
                    {year}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="divide-y divide-input ">
        {Object.entries(summary).map(([key, value]: [string, any]) => (
          <div key={key} className="flex  items-center text-sm justify-between py-2 h-">
            <Label name={key} groupName={LabelGroup.MANAGER_DASHBOARD} />
            <div className="text-sm font-bold">{value.toString()}</div>
          </div>
        ))}
      </CardContent>

      <CardFooter className="flex flex-col items-start gap-2 ">
        <Separator className="my-2" />
        <div className="flex items-center gap-x-2 w-full">
          <Button onClick={() => refetch()}>
            {isRefetching && <Spinner className="w-4 h-4 mr-2 text-primary-foreground" />}
            <Label name="update" groupName={LabelGroup.GLOBAL} />
          </Button>
          <ExportByFilterDialog yearId={selectedYear ?? undefined} />
        </div>
        <CardDescription>
          <span className="text-sm italic text-muted-foreground">
            <Label name="update_at" groupName={LabelGroup.MANAGER_DASHBOARD} />{' '}
            {new Date(dataUpdatedAt).toLocaleString('sv-SE', {
              hour: 'numeric',
              minute: 'numeric',
              second: 'numeric',
            })}
          </span>
        </CardDescription>
      </CardFooter>
    </Card>
  );
}
